import React, { FC, useCallback, useEffect, useState } from 'react';
import './BiologicalAgeListing.scss';
import {
  getMemberBioAgeDetails,
  publishBiologicalAge,
  getMemberBioAge,
  recalculateBiologicalAge,
  formatRecalculateBioAgeErrorMessage
} from '../../api/members';
import { toast } from 'react-toastify';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/store';
import { setBiologicalAge } from '../../store/memberSlice';
import { formatBiomarkerValueWithComparison } from '../../utils/biomarkers';
import Button from '../BaseComponents/Button';

const toastMultilineOpts = {
  style: { whiteSpace: 'pre-line' as const },
  autoClose: 12000
};

const BiologicalAgeAuditListing: FC = () => {
  const dispatch = useDispatch();
  const [bioAgeData, setBioAgeData] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const { memberData } = useSelector((state: RootState) => state.member);

  const headerColumns = [
    { title: 'Current Actual Age', key: 'actual_age', minWidth: '150px', flex: 0 },
    { title: 'Calculated Biological Age', key: 'calculated_bio_age', minWidth: '180px', flex: 0 },
    { title: 'Date of the Calculation', key: 'calculation_date', minWidth: '180px', flex: 0 },
    { title: 'Biomarkers Used (with Values)', key: 'biomarkers', minWidth: '250px', flex: 1 }
  ];

  const fetchBioAge = useCallback(
    async (opts?: { silent?: boolean }) => {
      const memberId = memberData?.user_id;
      if (!memberId) return;
      try {
        if (!opts?.silent) setIsLoading(true);
        const data = await getMemberBioAgeDetails(memberId);
        setBioAgeData(data);
      } catch (error: any) {
        toast.error(error.message || 'Failed to fetch biological age data');
        console.error(error);
      } finally {
        if (!opts?.silent) setIsLoading(false);
      }
    },
    [memberData?.user_id]
  );

  useEffect(() => {
    void fetchBioAge();
  }, [fetchBioAge]);

  const handleRecalculate = async () => {
    const memberId = memberData?.user_id;
    if (!memberId) return;
    setIsRecalculating(true);
    try {
      const result = await recalculateBiologicalAge(memberId);
      if (result.success) {
        toast.success(result.message || 'Biological age recalculated successfully.');
        if (result.missing_biomarkers?.length) {
          toast.info(
            `Missing biomarkers: ${result.missing_biomarkers.join(', ')}`
          );
        }
      } else {
        toast.error(
          formatRecalculateBioAgeErrorMessage(result.message, result.missing_biomarkers),
          toastMultilineOpts
        );
      }
      await fetchBioAge({ silent: true });
      try {
        const bioAgeResponse = await getMemberBioAge(memberId);
        const bioAgeValue = bioAgeResponse?.biological_age?.value || '';
        dispatch(setBiologicalAge(bioAgeValue));
      } catch (error) {
        console.error('Failed to fetch updated biological age:', error);
      }
    } catch (error: any) {
      toast.error(
        error?.message || 'Failed to recalculate biological age',
        toastMultilineOpts
      );
    } finally {
      setIsRecalculating(false);
    }
  };

  const handlePublishedButton = async (bioLogicalId: string | number, memberId: string) => {
    if (bioLogicalId) {
      try {
        const data = await publishBiologicalAge(bioLogicalId, memberId);
        if (data.success) {
          toast.success(data.message);
          // Refresh data after successful publish/unpublish
          await fetchBioAge();
          // Update the biological age in Redux store
          try {
            const bioAgeResponse = await getMemberBioAge(memberId);
            const bioAgeValue = bioAgeResponse?.biological_age?.value || "";
            dispatch(setBiologicalAge(bioAgeValue));
          } catch (error) {
            console.error('Failed to fetch updated biological age:', error);
          }
        } else {
          toast.error('Failed to publish biological age.');
        }
      } catch (error: any) {
        toast.error(error.message || 'Error publishing biological age.');
      }
    }
  };

  if (isLoading) return <p>Loading...</p>;
  if (!bioAgeData) return <p>No biological age data available.</p>;

  return (
    <div className="email-audit-container">
      <div className="biological-age-report-header sideBarPad2">
        <h3>Biological Age Report</h3>
        <Button
          text="Recalculate biological age"
          onClick={() => void handleRecalculate()}
          isLoading={isRecalculating}
          isDisabled={!memberData?.user_id || isLoading || isRecalculating}
          width="220px"
        />
      </div>
    <table className="email-table">
      <thead>
        <tr>
          {headerColumns.map((col) => (
            <th key={col.key} style={{ minWidth: col.minWidth }}>{col.title}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {bioAgeData.map((record : any,index:any) => (
          <tr key={record.id} style={{ backgroundColor: '#f7f9fc', borderRadius: "8px", marginBottom: "20px" }}>
            <td>{record.actual_age}</td>
            <td>{record.calculated_bio_age}{index === 0 && <p className='add-note-btn' onClick={() => handlePublishedButton(record.id, record.member_id)}>{record.status}</p>}</td>
            <td>{new Date(record.calculation_date).toLocaleDateString(undefined, {year: 'numeric', month: '2-digit', day: '2-digit'})} </td>
            <td>
              <ul className="biomarker-list">
                {(record.biomarkers_used || []).map((bm: any, bmIndex: number) => (
                  <li key={bm.biomarker_id ?? `${record.id}-${bmIndex}`}>
                    {bm.biomarker_name}: <strong>{formatBiomarkerValueWithComparison(bm.value, bm.unit)}</strong>
                  </li>
                ))}
              </ul>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
  );
};

export default BiologicalAgeAuditListing;
